import * as UI from "@iyk/ui"
import * as React from "react"

import { Profile } from "../profile.ts"
import { AudioPlayer } from "./audio-player/audio-player.tsx"
import { PlayPauseButton } from "./audio-player/play-pause-button.tsx"
import { useAudioPlayer } from "./audio-player/use-audio-player.ts"
import { Sheet } from "./sheet.tsx"
import { useExclusiveSheet } from "./use-exclusive-sheet.ts"

export const ExclusivePlaylistCard = (playlist: Profile.Playlist) => {
  const [openSheet, setOpenSheet] = useExclusiveSheet(playlist.id)

  return (
    <Sheet.Root open={openSheet} onOpenChange={setOpenSheet}>
      <Sheet.Trigger asChild>
        <div className="cursor-pointer">
          <PlaylistCard playlist={playlist} />
        </div>
      </Sheet.Trigger>
      <Sheet.Content title={playlist.title} description={playlist.artist}>
        <PlaylistInfo playlist={playlist} />
        <TrackList playlist={playlist} />
        <AudioPlayer />
      </Sheet.Content>
    </Sheet.Root>
  )
}

function PlaylistCard({ playlist }: { playlist: Profile.Playlist }) {
  const { isPlaying, currentTrack, playPlaylist, togglePlayPause } = useAudioPlayer()

  const isCurrentTrackInPlaylist = currentTrack?.playlist.id === playlist.id
  const isCurrentPlaylistPlaying = isCurrentTrackInPlaylist && isPlaying

  const handlePlayPauseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    if (isCurrentTrackInPlaylist) {
      togglePlayPause()
    } else {
      playPlaylist(playlist)
    }
  }

  return (
    <div className="flex h-36 overflow-hidden rounded bg-grayA-2 relative">
      <img
        src={playlist.coverImageUrl}
        alt={playlist.title}
        className="aspect-square object-cover shrink-0"
      />
      <div className="flex-1 flex flex-col justify-between px-3 py-2 text-left gap-4">
        <div className="flex flex-col items-start gap-2">
          <div>
            <p>{playlist.title}</p>
            <p className="text-sm text-gray-11">{playlist.artist}</p>
          </div>
          {Profile.Playlist.$match(playlist, {
            Released: () => null,
            PartiallyReleased: (p) => <ReleaseDateTag releaseDate={p.releaseDate} />,
            Unreleased: (p) => <ReleaseDateTag releaseDate={p.releaseDate} />,
          })}
        </div>

        <p className="text-sm text-gray-11">{playlist.tracks.length} 곡</p>
      </div>

      {!isPlaylistUnreleased(playlist) && (
        <div className="absolute right-2 bottom-2">
          <PlayPauseButton isPlaying={isCurrentPlaylistPlaying} onClick={handlePlayPauseClick} />
        </div>
      )}
    </div>
  )
}

function PlaylistInfo({ playlist }: { playlist: Profile.Playlist }) {
  return (
    <div className="flex flex-col items-center text-center gap-4 px-4 py-6 bg-gray-2">
      <img
        src={playlist.coverImageUrl}
        alt={playlist.title}
        className="size-28 rounded-sm object-cover overflow-hidden shrink-0"
      />
      <div className="flex flex-col items-center justify-center gap-3">
        {Profile.Playlist.$match(playlist, {
          Released: () => null,
          PartiallyReleased: (p) => p.releaseDate && <ReleaseDateTag releaseDate={p.releaseDate} />,
          Unreleased: (p) => <ReleaseDateTag releaseDate={p.releaseDate} />,
        })}
        <div>
          <div className="mb-1">
            <p>{playlist.title}</p>
            <p className="text-sm text-gray-11">{playlist.artist}</p>
          </div>
          <p className="text-sm text-gray-11">{playlist.tracks.length} 곡</p>
        </div>
      </div>
    </div>
  )
}

function TrackList({ playlist }: { playlist: Profile.Playlist }) {
  return (
    <div className="max-h-full overflow-auto p-2">
      {playlist.tracks.map((track, index) => (
        <TrackListItem
          key={`track-${track.id}`}
          position={index + 1}
          track={track}
          playlist={playlist}
        />
      ))}
    </div>
  )
}

function TrackListItem({
  track,
  position,
  playlist,
}: {
  track: Profile.Track
  position: number
  playlist: Profile.Playlist
}) {
  const { isPlaying, currentTrack, playTrack } = useAudioPlayer()

  const isCurrent = currentTrack?.id === track.id
  const isReleased = isTrackReleased(track)

  return (
    <button
      type="button"
      className={UI.cx([
        "w-full flex items-center gap-3 py-2 px-3",
        "data-[current=true]:bg-gray-3",
        "disabled:cursor-not-allowed",
        "group",
      ])}
      disabled={!isReleased}
      data-current={isCurrent}
      data-released={isReleased}
      onClick={() => playTrack(track.id, playlist)}
    >
      <div
        className={UI.cx([
          "w-4 text-xs shrink-0 text-left text-gray-11",
          "group-data-[released=false]:text-gray-10",
        ])}
      >
        {isCurrent && isPlaying ? <UI.Icon.SoundWaveSymbol aria-hidden="true" /> : position}
      </div>
      <div className="flex-1 space-y-0.5 text-left overflow-hidden ">
        <p
          className={UI.cx([
            "text-sm truncate text-ellipsis",
            "group-data-[released=false]:text-gray-10",
          ])}
        >
          {track.title}
        </p>
        <p
          className={UI.cx([
            "text-xs text-gray-11 truncate text-ellipsis",
            "group-data-[released=false]:text-gray-10",
          ])}
        >
          {track.artist}
        </p>
      </div>
      <div className="shrink-0">
        {isReleased && (
          <div className="tabular-nums text-xs text-gray-11">
            {isCurrent ? (
              <TrackCurrentTime trackDuration={track.duration} />
            ) : (
              formatDuration(track.duration)
            )}
          </div>
        )}
      </div>
    </button>
  )
}

function TrackCurrentTime({ trackDuration }: { trackDuration: number }) {
  const { currentTrackTime } = useAudioPlayer()
  return <span>{`${formatDuration(currentTrackTime)}/${formatDuration(trackDuration)}`}</span>
}

function ReleaseDateTag({ releaseDate }: { releaseDate: string }) {
  return (
    <span className="text-xs text-gray-11 px-1 py-0.5 bshadow bshadow-gray-6">
      사용 가능 {formatReleaseDate(releaseDate)}
    </span>
  )
}

function formatReleaseDate(releaseDate: string): string {
  return new Date(releaseDate).toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  })
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`
}

function isTrackReleased(track: Profile.Track) {
  return Profile.Track.$is("Released")(track)
}

function isPlaylistUnreleased(playlist: Profile.Playlist) {
  return Profile.Playlist.$is("Unreleased")(playlist)
}
